import { useEffect, useState, useRef } from "react";
import { useOasisBackend } from "../../hooks/useOasisBackend";
import { toast, ToastContainer } from "react-toastify";
import { SemanticIndexRequest, OasisIndexRequest } from "../../api/OasisBackendApi";

const IndexRequestManager = ({databaseId, entityCollectionId}: {databaseId: string, entityCollectionId: string}) => {
    const db = useOasisBackend();

    const [semanticIndexRequests, setSemanticIndexRequests] = useState<SemanticIndexRequest[]>([]);
    const [oasisIndexRequests, setOasisIndexRequests] = useState<OasisIndexRequest[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const isFetchingRef = useRef(false);
    const [showRedoModal, setShowRedoModal] = useState(false);
    const [showOasisConfigModal, setShowOasisConfigModal] = useState(false);
    const [redoConfig, setRedoConfig] = useState({
        max_dimensions: 25,
        distance_metric: "cosine" as "cosine" | "euclidean" | "manhattan"
    });
    const [oasisConfig, setOasisConfig] = useState({
        max_dimensions: 25,
        entity_min: 100,
        candidate_dimensions: 100,
        distance_metric: "cosine" as "cosine" | "euclidean" | "manhattan"
    });

    const indexEntities = async (index_type: string, config?: any) => {
        const requestBody: any = {index_type: index_type as "semantic" | "oasis"};
        if (config) {
            requestBody.config = config;
        }
        const response = await db.endpoints.api.apiDatabaseIndexEntityCollectionCreate(databaseId, requestBody);
        if (response.status === 201) {
            toast.success("Entities indexing processing. This will take a while. We will email you when it's done.");
            // Start polling immediately after initiating a new request
            fetchIndexRequests();
        } else {
            toast.error("Failed to index entities");
        }
    }

    const handleIndexOasis = () => {
        setShowOasisConfigModal(true);
    }

    const confirmIndexOasis = async () => {
        await indexEntities("oasis", oasisConfig);
        setShowOasisConfigModal(false);
    }

    const hasCompletedOasisRequest = () => {
        return oasisIndexRequests.some(request => request.status === "completed");
    }

    const getLatestCompletedOasisRequest = (): OasisIndexRequest | null => {
        const completed = oasisIndexRequests.filter(request => request.status === "completed");
        if (completed.length === 0) return null;
        // Return the most recent one (assuming they're sorted by date, or we can sort by id)
        return completed[0];
    }

    const redoSelection = async () => {
        if (!hasCompletedOasisRequest()) {
            toast.error("No completed oasis index request found. Please run a full oasis index first.");
            return;
        }

        const latestRequest = getLatestCompletedOasisRequest();
        if (!latestRequest) {
            toast.error("No completed oasis index request found.");
            return;
        }

        try {
            const response = await db.endpoints.api.apiDatabaseRedoOasisIndexSelectionCreate(databaseId, {
                source_request_id: latestRequest.id?.toString(),
                config: {
                    max_dimensions: redoConfig.max_dimensions,
                    distance_metric: redoConfig.distance_metric
                }
            });
            if (response.status === 201) {
                toast.success("Redo selection processing. This will take a while. We will email you when it's done.");
                setShowRedoModal(false);
                // Start polling immediately after initiating a new request
                fetchIndexRequests();
            } else {
                toast.error("Failed to redo selection");
            }
        } catch (error: any) {
            console.error("Error redoing selection:", error);
            toast.error(error.response?.data?.error || "Failed to redo selection");
        }
    }

    const fetchIndexRequests = async () => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        try {
            const response = await db.endpoints.api.apiEntityCollectionIndexRequestsRetrieve(entityCollectionId);
            setSemanticIndexRequests(response.data.semantic_index_requests);
            setOasisIndexRequests(response.data.oasis_index_requests);
        } catch (error) {
            console.error("Error fetching index requests:", error);
            toast.error("Failed to fetch index requests");
        } finally {
            setIsLoading(false);
            isFetchingRef.current = false;
        }
    }
    
    // Check if there are any requests not at failed or completed
    const hasActiveRequests = () => {
        const allRequests = [...semanticIndexRequests, ...oasisIndexRequests];
        return allRequests.some(request =>
            request.status !== "failed" && request.status !== "completed"
        );
    };

    // Check if there are any semantic requests not at failed or completed
    const hasActiveSemanticRequests = () => {
        return semanticIndexRequests.some(request =>
            request.status !== "failed" && request.status !== "completed"
        );
    };

    // Check if there are any oasis requests not at failed or completed
    const hasActiveOasisRequests = () => {
        return oasisIndexRequests.some(request =>
            request.status !== "failed" && request.status !== "completed"
        );
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "completed":
                return "text-green-400";
            case "failed":
                return "text-red-400";
            case "processing":
                return "text-yellow-400";
            case "pending":
                return "text-blue-400";
            default:
                return "text-gray-400";
        }
    };

    useEffect(() => {
        fetchIndexRequests();
    }, []);

    useEffect(() => {
        if (!hasActiveRequests()) return;

        const pollInterval = setInterval(() => {
            fetchIndexRequests();
        }, 20000); // Poll every 2 seconds

        return () => clearInterval(pollInterval);
    }, [semanticIndexRequests, oasisIndexRequests]);

    // Show loading screen if data hasn't been loaded yet
    if (isLoading) {
        return (
            <div className="space-y-4 sm:space-y-6">
                <div className="bg-gray-700 rounded-lg p-4 sm:p-6">
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mr-3"></div>
                        <span className="text-white text-lg">Loading index requests...</span>
                    </div>
                </div>
            </div>
        );
    }

    return(
        <div className="space-y-4 sm:space-y-6">
            
            <div className="bg-gray-700 rounded-lg p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">Index Actions</h3>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <button 
                        onClick={() => indexEntities("semantic")}
                        disabled={hasActiveSemanticRequests()}
                        className={`px-4 sm:px-6 py-2 sm:py-3 font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 text-sm sm:text-base ${
                            hasActiveSemanticRequests() 
                                ? 'bg-gray-500 text-gray-300 cursor-not-allowed' 
                                : 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                        }`}
                    >
                        Index Semantic
                    </button>
                    <button 
                        onClick={handleIndexOasis}
                        disabled={hasActiveOasisRequests()}
                        className={`px-4 sm:px-6 py-2 sm:py-3 font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 text-sm sm:text-base ${
                            hasActiveOasisRequests() 
                                ? 'bg-gray-500 text-gray-300 cursor-not-allowed' 
                                : 'bg-purple-600 hover:bg-purple-700 text-white focus:ring-purple-500'
                        }`}
                    >
                        Index Oasis
                    </button>
                    <button 
                        onClick={() => setShowRedoModal(true)}
                        disabled={hasActiveOasisRequests() || !hasCompletedOasisRequest()}
                        className={`px-4 sm:px-6 py-2 sm:py-3 font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 text-sm sm:text-base ${
                            (hasActiveOasisRequests() || !hasCompletedOasisRequest())
                                ? 'bg-gray-500 text-gray-300 cursor-not-allowed' 
                                : 'bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-500'
                        }`}
                        title={!hasCompletedOasisRequest() ? "No completed oasis index request found. Run a full oasis index first." : "Redo selection with different parameters"}
                    >
                        Redo Selection
                    </button>
                </div>
                {(hasActiveSemanticRequests() || hasActiveOasisRequests()) && (
                    <p className="text-yellow-400 text-sm mt-2">
                        Index buttons are disabled while requests are processing
                    </p>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div className="bg-gray-700 rounded-lg p-4 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4 flex items-center">
                        <span className="w-2 h-2 sm:w-3 sm:h-3 bg-blue-500 rounded-full mr-2 sm:mr-3"></span>
                        Semantic Index Requests
                    </h3>
                    <div className="space-y-2 sm:space-y-3">
                        {semanticIndexRequests && semanticIndexRequests.length > 0 ? (
                            semanticIndexRequests.map((request) => (
                                <div key={request.id} className="bg-gray-600 rounded-lg p-3 sm:p-4 border border-gray-500">
                                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0">
                                        <span className="text-xs sm:text-sm text-gray-300">ID: {request.id}</span>
                                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status || 'unknown')}`}>
                                            {request.status || 'unknown'}
                                        </span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-gray-500 text-center py-6 sm:py-8">
                                <p className="text-sm sm:text-base">No semantic index requests found</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-gray-700 rounded-lg p-4 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4 flex items-center">
                        <span className="w-2 h-2 sm:w-3 sm:h-3 bg-purple-500 rounded-full mr-2 sm:mr-3"></span>
                        Oasis Index Requests
                    </h3>
                    <div className="space-y-2 sm:space-y-3">
                        {oasisIndexRequests && oasisIndexRequests.length > 0 ? (
                            oasisIndexRequests.map((request) => (
                                <div key={request.id} className="bg-gray-600 rounded-lg p-3 sm:p-4 border border-gray-500">
                                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0">
                                        <span className="text-xs sm:text-sm text-gray-300">ID: {request.id}</span>
                                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status || 'unknown')}`}>
                                            {request.status || 'unknown'}
                                        </span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-gray-500 text-center py-6 sm:py-8">
                                <p className="text-sm sm:text-base">No oasis index requests found</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {hasActiveRequests() && (
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center">
                        <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-blue-400 mr-2 sm:mr-3"></div>
                        <span className="text-blue-400 text-sm sm:text-base">Polling for updates...</span>
                    </div>
                </div>
            )}

            {/* Oasis Index Configuration Modal */}
            {showOasisConfigModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
                        <h3 className="text-xl font-semibold text-white mb-4">Configure Oasis Index</h3>
                        <p className="text-gray-300 text-sm mb-4">
                            Configure parameters for the Oasis indexing process.
                        </p>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Max Dimensions
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={oasisConfig.max_dimensions}
                                    onChange={(e) => setOasisConfig({...oasisConfig, max_dimensions: parseInt(e.target.value) || 25})}
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    placeholder="25"
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                    Maximum number of dimensions to select (default: 25)
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Entity Min
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    value={oasisConfig.entity_min}
                                    onChange={(e) => setOasisConfig({...oasisConfig, entity_min: parseInt(e.target.value) || 100})}
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    placeholder="100"
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                    Minimum number of entities required for indexing (default: 100)
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Candidate Dimensions
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    value={oasisConfig.candidate_dimensions}
                                    onChange={(e) => setOasisConfig({...oasisConfig, candidate_dimensions: parseInt(e.target.value) || 100})}
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    placeholder="100"
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                    Number of candidate dimensions to generate (default: 100)
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Distance Metric
                                </label>
                                <select
                                    value={oasisConfig.distance_metric}
                                    onChange={(e) => setOasisConfig({...oasisConfig, distance_metric: e.target.value as "cosine" | "euclidean" | "manhattan"})}
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                >
                                    <option value="cosine">Cosine</option>
                                    <option value="euclidean">Euclidean</option>
                                    <option value="manhattan">Manhattan</option>
                                </select>
                                <p className="text-xs text-gray-400 mt-1">
                                    Distance metric to use for dimension selection (default: cosine)
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-3 justify-end mt-6">
                            <button
                                onClick={() => setShowOasisConfigModal(false)}
                                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmIndexOasis}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                            >
                                Start Indexing
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Redo Selection Modal */}
            {showRedoModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-700">
                        <h3 className="text-xl font-semibold text-white mb-4">Redo Oasis Selection</h3>
                        <p className="text-gray-300 text-sm mb-4">
                            This will rerun only the selection step using the exact same queries, entities, and candidate dimensions from your last completed oasis index request.
                        </p>
                        <div className="space-y-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Max Dimensions
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={redoConfig.max_dimensions}
                                    onChange={(e) => setRedoConfig({...redoConfig, max_dimensions: parseInt(e.target.value) || 25})}
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="25"
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                    Maximum number of dimensions to select (default: 25)
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Distance Metric
                                </label>
                                <select
                                    value={redoConfig.distance_metric}
                                    onChange={(e) => setRedoConfig({...redoConfig, distance_metric: e.target.value as "cosine" | "euclidean" | "manhattan"})}
                                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    <option value="cosine">Cosine</option>
                                    <option value="euclidean">Euclidean</option>
                                    <option value="manhattan">Manhattan</option>
                                </select>
                                <p className="text-xs text-gray-400 mt-1">
                                    Distance metric to use for dimension selection (default: cosine)
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowRedoModal(false)}
                                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={redoSelection}
                                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                            >
                                Run Selection
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer />
        </div>
    )
}

export default IndexRequestManager;